import PropTypes from 'prop-types';
//
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authActions, authSelectors, authTypes } from 'app-components/flux/auth';
import { datasetsActions, datasetsUtils } from 'app-components/flux/datasets';
import { isDevBuild } from 'app-components/utils/core';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SwitchDatasetIcon from '@mui/icons-material/KeyboardTab';
import ShareIcon from '@mui/icons-material/ShareOutlined';
import SupervisedUserIcon from '@mui/icons-material/SupervisedUserCircle';
import Chip from '@mui/material/Chip';

import { QDropdownMenu, QMenuDivider, QMenuItem } from 'components/QDropdowns';

import AddShareDialog from './AddShareDialog';
import ShowManageSharesDialog from './ManageSharesDialog';
import UpsertDatasetDialog from './UpsertDatasetDialog';

const OwnedDatasetMenu = (props) => {
  const { dataset, shareCounts, shares } = props;
  const dispatch = useDispatch();

  const [sharesUsed, sharesAllowed] = shareCounts;
  const sharesLeft = Math.max(sharesAllowed - sharesUsed, 0);

  const [showAddShareDialog, setShowAddShareDialog] = useState(false);
  const [showManageSharesDialog, setShowManageSharesDialog] = useState(false);
  const [showUpsertDatasetDialog, setShowUpsertDatasetDialog] = useState(false);

  const activeDatasetId = useSelector((state) => authSelectors.getDatasetId(state));

  const hasActiveEntitlement = useMemo(() => datasetsUtils.hasActiveEntitlement(dataset), [dataset]);

  const handleInviteUser = () => {
    setShowAddShareDialog(true);
  };

  const handleManageUsers = () => {
    setShowManageSharesDialog(true);
  };

  const handleUpsertDataset = () => {
    setShowUpsertDatasetDialog(true);
  };

  const handleSelectDataset = () => {
    dispatch(authActions.authSelectDataset(authTypes.AuthSelectDatasetPayload({ dataset, userChoice: true })));
  };

  const handleDeleteDataset = () => {
    dispatch(
      datasetsActions.deleteDataset(
        { id: dataset.id },
        { undo: { userMessage: 'Space deleted', disableUndoAction: true } },
      ),
    );
  };

  return (
    <>
      {showAddShareDialog && (
        <AddShareDialog dataset={dataset} onClose={() => setShowAddShareDialog(false)} shareCounts={shareCounts} />
      )}
      {showManageSharesDialog && (
        <ShowManageSharesDialog dataset={dataset} onClose={() => setShowManageSharesDialog(false)} shares={shares} />
      )}
      {showUpsertDatasetDialog && (
        <UpsertDatasetDialog dataset={dataset} onClose={() => setShowUpsertDatasetDialog(false)} />
      )}
      <QDropdownMenu
        id={`dataset-menu-button-${dataset.id}`}
        aria-label='Space options'
        iconButtonVariant='plain'
        iconButtonSize='small'
        disabled={!hasActiveEntitlement}
      >
        <QMenuItem
          variant='action'
          label='Switch to this space'
          id='switch-space-button'
          disabled={dataset.id === activeDatasetId}
          startIcon={<SwitchDatasetIcon fontSize='small' />}
          onClick={handleSelectDataset}
        />
        <QMenuDivider />
        <QMenuItem
          variant='action'
          label='Update this space'
          id='update-space-button'
          startIcon={<EditIcon fontSize='small' />}
          onClick={handleUpsertDataset}
        />
        <QMenuDivider />
        <QMenuItem
          variant='action'
          label={sharesUsed && sharesLeft ? `Invite member (${sharesLeft})` : 'Invite member'}
          id='invite-space-button'
          disabled={!sharesLeft}
          startIcon={<ShareIcon fontSize='small' />}
          onClick={handleInviteUser}
        />
        <QMenuItem
          variant='action'
          label='Manage members'
          id='manage-space-button'
          disabled={!shares || shares.length === 0}
          startIcon={<SupervisedUserIcon fontSize='small' />}
          onClick={handleManageUsers}
        />
        {isDevBuild() ? <QMenuDivider /> : null}
        {isDevBuild() ? (
          <QMenuItem
            variant='action'
            label='Delete'
            id='delete-space-button'
            destructive
            startIcon={<DeleteOutlineIcon fontSize='small' />}
            closeOnClick
            endAction={<Chip label='Dev only' size='small' variant='tonal' color='warning' />}
            onClick={handleDeleteDataset}
          />
        ) : null}
      </QDropdownMenu>
    </>
  );
};

export default OwnedDatasetMenu;

OwnedDatasetMenu.propTypes = {
  dataset: PropTypes.object,
  shareCounts: PropTypes.arrayOf(PropTypes.number),
  shares: PropTypes.arrayOf(PropTypes.object),
};
